// Lightweight fade-in library, triggered purely by data attributes — no
// widget settings, no dependency. Add data-sh-fade to any element:
//
//   data-sh-fade            required — enables the effect. Value sets the
//                           direction the element travels in from:
//                           "up" (default) | "down" | "left" | "right" | "none"
//   data-sh-fade-delay      ms, default 0
//   data-sh-fade-duration   ms, default 600
//   data-sh-fade-distance   px it travels, default 24
//   data-sh-fade-threshold  0–1, how much must be visible to trigger, default 0.15
//   data-sh-fade-once       "false" to re-trigger every time it re-enters view
//                           (default: true — fades in once, then stays)
//   data-sh-fade-root-margin  CSS margin syntax, shifts the trigger point —
//                           e.g. "0px 0px -200px 0px" fires 200px before the
//                           element reaches the bottom of the viewport.
//                           Default "0px" (fires right at the viewport edge).
//
// The hidden (pre-animation) state is defined in CSS, not here — see
// [data-sh-fade] in global.css. That's what's loaded and applied before
// first paint; this module only fine-tunes custom values (while the element
// is still invisible, so no flash) and toggles "sh-fade-in" to reveal it.
// Honors prefers-reduced-motion (handled in CSS — this module just skips).

const DEFAULTS = {
    duration: 600,
    delay: 0,
    distance: 24,
    threshold: 0.15,
    once: true,
    rootMargin: '0px',
};

function offsetFor(direction, distance) {
    switch (direction) {
        case 'down':  return { x: 0, y: -distance };
        case 'left':  return { x: distance, y: 0 };
        case 'right': return { x: -distance, y: 0 };
        case 'none':  return { x: 0, y: 0 };
        case 'up':
        default:      return { x: 0, y: distance };
    }
}

function readConfig(el) {
    const direction = el.dataset.shFade || 'up';
    return {
        direction,
        delay: parseInt(el.dataset.shFadeDelay, 10) || DEFAULTS.delay,
        duration: parseInt(el.dataset.shFadeDuration, 10) || DEFAULTS.duration,
        distance: el.dataset.shFadeDistance !== undefined
            ? parseInt(el.dataset.shFadeDistance, 10)
            : DEFAULTS.distance,
        threshold: el.dataset.shFadeThreshold !== undefined
            ? parseFloat(el.dataset.shFadeThreshold)
            : DEFAULTS.threshold,
        once: el.dataset.shFadeOnce !== 'false',
        rootMargin: el.dataset.shFadeRootMargin || DEFAULTS.rootMargin,
    };
}

// Apply any non-default duration/delay/distance as inline styles. Safe to do
// any time before reveal — the element is still hidden via CSS, so adjusting
// these causes no visible flash.
function applyCustomValues(el, config) {
    if (config.duration !== DEFAULTS.duration || config.delay !== DEFAULTS.delay) {
        el.style.transitionDuration = `${config.duration}ms`;
        el.style.transitionDelay = `${config.delay}ms`;
    }
    if (config.distance !== DEFAULTS.distance && config.direction !== 'none') {
        const { x, y } = offsetFor(config.direction, config.distance);
        el.style.transform = `translate3d(${x}px, ${y}px, 0)`;
    }
}

export default function initFadeIn() {
    const elements = document.querySelectorAll('[data-sh-fade]');
    if (!elements.length) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) {
        elements.forEach((el) => el.classList.add('sh-fade-in'));
        return;
    }

    const configs = new Map();
    const groups = new Map();

    elements.forEach((el) => {
        const config = readConfig(el);
        configs.set(el, config);
        applyCustomValues(el, config);

        const key = `${config.threshold}|${config.rootMargin}`;
        if (!groups.has(key)) {
            groups.set(key, { threshold: config.threshold, rootMargin: config.rootMargin, els: [] });
        }
        groups.get(key).els.push(el);
    });

    // IntersectionObserver only takes one threshold/rootMargin pair per
    // instance, so group elements that share both and spin up one observer
    // per group.
    groups.forEach(({ threshold, rootMargin, els }) => {
        const observer = new IntersectionObserver((entries, obs) => {
            entries.forEach((entry) => {
                const config = configs.get(entry.target);
                if (entry.isIntersecting) {
                    entry.target.classList.add('sh-fade-in');
                    if (config.once) {
                        obs.unobserve(entry.target);
                    }
                } else if (!config.once) {
                    entry.target.classList.remove('sh-fade-in');
                }
            });
        }, { threshold, rootMargin });

        els.forEach((el) => observer.observe(el));
    });
}
